// ============= Product Form Taxonomy =============
// Single source of truth for all product forms, organized by category.
// Based on ICHS industry standard categorization.

/** Built-in categories; admins can add more at runtime (product_form_categories). */
export type ProductFormCategory = 'Biomass' | 'Pre-Rolls & Joints' | 'Concentrates' | 'Extracts' | (string & {});

export interface ProductFormItem {
  value: string;      // lowercase slug, stored in DB
  label: string;      // display name
  category: ProductFormCategory;
  /**
   * Optional natural-language phrasings users say that map to THIS leaf.
   * Mirrored into supabase/functions/_shared/search-taxonomy.ts (FormEntry.vernacular)
   * which feeds the AI search parser's VERNACULAR prompt block. If you change
   * the list here, change the edge mirror too.
   */
  vernacular?: string[];
}

export const PRODUCT_FORM_CATEGORIES: ProductFormCategory[] = [
  'Biomass',
  'Pre-Rolls & Joints',
  'Concentrates',
  'Extracts',
];

export const PRODUCT_FORM_ITEMS: ProductFormItem[] = [
  // ── Biomass (12 items) ──
  { value: 'a-flower',                  label: 'A-Flower',                   category: 'Biomass',
    vernacular: ['A flower','A-flower','A grade flower','A-grade flower','grade A flower','premium A flower','top shelf A','top-shelf A flower'] },
  { value: 'b-flower',                  label: 'B-Flower',                   category: 'Biomass',
    vernacular: ['B flower','B-flower','B grade flower','B-grade flower','grade B flower','mid-grade flower','mids'] },
  { value: 'c-flower',                  label: 'C-Flower',                   category: 'Biomass',
    vernacular: ['C flower','C-flower','C grade flower','C-grade flower','grade C flower','low grade flower','value flower'] },
  { value: 'a/b flower mix',            label: 'A/B Flower Mix',             category: 'Biomass',
    vernacular: ['A/B flower','AB flower','A B mix','A/B mix','A and B mix','A/B blend'] },
  { value: 'b/c flower mix',            label: 'B/C Flower Mix',             category: 'Biomass',
    vernacular: ['B/C flower','BC flower','B C mix','B/C mix','B and C mix','B/C blend'] },
  { value: 'a/b/c flower mix',          label: 'A/B/C Flower Mix',           category: 'Biomass',
    vernacular: ['A/B/C flower','ABC flower','A B C mix','A/B/C mix','A/B/C blend'] },
  { value: 'fan leaf',                   label: 'Fan Leaf',                   category: 'Biomass' },
  { value: 'fresh frozen',              label: 'Fresh Frozen',               category: 'Biomass' },
  { value: 'milled flower',             label: 'Milled Flower',              category: 'Biomass' },
  { value: 'mixed material',            label: 'Mixed Material',             category: 'Biomass' },
  { value: 'trim',                       label: 'Trim',                       category: 'Biomass' },
  { value: 'clone',                      label: 'Clone',                      category: 'Biomass' },

  // ── Pre-Rolls & Joints (6 items) ──
  { value: 'pre-roll, trim',            label: 'Pre-Roll, Trim',             category: 'Pre-Rolls & Joints' },
  { value: 'pre-roll, flower-trim blend', label: 'Pre-Roll, Flower-Trim Blend', category: 'Pre-Rolls & Joints' },
  { value: 'pre-roll, flower',          label: 'Pre-Roll, Flower',           category: 'Pre-Rolls & Joints' },
  { value: 'infused pre-roll, trim',            label: 'Infused Pre-Roll, Trim',             category: 'Pre-Rolls & Joints' },
  { value: 'infused pre-roll, flower-trim blend', label: 'Infused Pre-Roll, Flower-Trim Blend', category: 'Pre-Rolls & Joints' },
  { value: 'infused pre-roll, flower',          label: 'Infused Pre-Roll, Flower',           category: 'Pre-Rolls & Joints' },

  // ── Concentrates (10 items) ──
  { value: 'bubble hash',    label: 'Bubble Hash',    category: 'Concentrates' },
  { value: 'charas',         label: 'Charas',         category: 'Concentrates' },
  { value: 'full melt',      label: 'Full Melt',      category: 'Concentrates' },
  { value: 'hash',           label: 'Hash',           category: 'Concentrates' },
  { value: 'ice water hash', label: 'Ice Water Hash', category: 'Concentrates' },
  { value: 'kief',           label: 'Kief',           category: 'Concentrates' },
  { value: 'rosin (cold cured)', label: 'Rosin (Cold Cured)', category: 'Concentrates' },
  { value: 'rosin (warm cured)', label: 'Rosin (Warm Cured)', category: 'Concentrates' },
  { value: 'live rosin',     label: 'Live Rosin',     category: 'Concentrates' },
  { value: 'hash rosin',     label: 'Hash Rosin',     category: 'Concentrates' },

  // ── Extracts (19 items) ──
  { value: 'badder/batter',  label: 'Badder/Batter',  category: 'Extracts' },
  { value: 'budder',         label: 'Budder',         category: 'Extracts' },
  { value: 'cake icing',     label: 'Cake Icing',     category: 'Extracts' },
  { value: 'crumble',        label: 'Crumble',        category: 'Extracts' },
  { value: 'crystals',       label: 'Crystals',       category: 'Extracts' },
  { value: 'custard',        label: 'Custard',        category: 'Extracts' },
  { value: 'distillate',     label: 'Distillate',     category: 'Extracts' },
  { value: 'diamonds',       label: 'Diamonds',       category: 'Extracts' },
  { value: 'frosting',       label: 'Frosting',       category: 'Extracts' },
  { value: 'full extract cannabis oil', label: 'Full Extract Cannabis Oil', category: 'Extracts',
    vernacular: ['FECO','full extract cannabis oil','full-extract cannabis oil','RSO-style oil'] },
  { value: 'pull and snap',  label: 'Pull and Snap',  category: 'Extracts' },
  { value: 'sap',            label: 'Sap',            category: 'Extracts' },
  { value: 'sauce/hte',      label: 'Sauce/HTE',      category: 'Extracts' },
  { value: 'shatter',        label: 'Shatter',        category: 'Extracts' },
  { value: 'sugar',          label: 'Sugar',          category: 'Extracts' },
  { value: 'taffy',          label: 'Taffy',          category: 'Extracts' },
  { value: 'wax',            label: 'Wax',            category: 'Extracts' },
  { value: 'isolate',        label: 'Isolate',        category: 'Extracts' },
  { value: 'live resin',     label: 'Live Resin',     category: 'Extracts' },
];

/**
 * Standard types added by system admins on /admin/product-types live in
 * `product_form_terms`. They are merged into PRODUCT_FORM_ITEMS at runtime so
 * seller pickers and buyer filters show them without a code change.
 */
function toLabel(value: string): string {
  return value.replace(/\b([a-z])/g, (m) => m.toUpperCase()).replace(/\bBho\b/g, 'BHO');
}

export function registerDynamicProductForms(
  terms: { value: string; category: string }[],
): boolean {
  let changed = false;
  for (const t of terms) {
    if (!t.category) continue;
    if (!PRODUCT_FORM_CATEGORIES.includes(t.category)) {
      PRODUCT_FORM_CATEGORIES.push(t.category);
      changed = true;
    }
    const existing = PRODUCT_FORM_ITEMS.find((i) => i.value === t.value);
    if (existing) {
      if (existing.category !== t.category) {
        existing.category = t.category as ProductFormCategory;
        changed = true;
      }
      continue;
    }
    PRODUCT_FORM_ITEMS.push({ value: t.value, label: toLabel(t.value), category: t.category as ProductFormCategory });
    PRODUCT_FORMS_FLAT_MUTABLE.push({ value: t.value, label: toLabel(t.value) });
    changed = true;
  }
  return changed;
}

let dynamicLoad: Promise<void> | null = null;
/** Fetch admin-added standard types once per session (call again with force after edits). */
export function loadDynamicProductForms(force = false): Promise<void> {
  if (dynamicLoad && !force) return dynamicLoad;
  dynamicLoad = import('@/integrations/supabase/client').then(async ({ supabase }) => {
    const [{ data: cats }, { data }] = await Promise.all([
      supabase.from('product_form_categories').select('name, sort_order').order('sort_order').order('name'),
      supabase.from('product_form_terms').select('value, category'),
    ]);
    for (const c of (cats ?? []) as { name: string }[]) {
      if (!PRODUCT_FORM_CATEGORIES.includes(c.name)) PRODUCT_FORM_CATEGORIES.push(c.name);
    }
    if (data) registerDynamicProductForms(data as { value: string; category: string }[]);
  }).catch(() => undefined);
  return dynamicLoad;
}

/** Get items grouped by category (in canonical order) */
export function getGroupedProductForms(): Record<ProductFormCategory, ProductFormItem[]> {
  const grouped = {} as Record<ProductFormCategory, ProductFormItem[]>;
  for (const c of PRODUCT_FORM_CATEGORIES) grouped[c] = [];
  for (const item of PRODUCT_FORM_ITEMS) {
    (grouped[item.category] ??= []).push(item);
  }
  return grouped;
}

/** Legacy product forms — old values still present in DB, available for filtering but not for new listings */
export const LEGACY_PRODUCT_FORMS: ProductFormItem[] = [
  { value: 'flower',      label: 'Flower',      category: 'Biomass' },
  { value: 'trim',         label: 'Trim',         category: 'Biomass' },
  { value: 'shake',        label: 'Shake',        category: 'Biomass' },
  { value: 'pre-rolls',    label: 'Pre-Rolls',    category: 'Biomass' },
  { value: 'concentrate',  label: 'Concentrate',  category: 'Concentrates' },
  { value: 'edibles',      label: 'Edibles',      category: 'Extracts' },
  { value: 'feco',         label: 'Full Extract Cannabis Oil', category: 'Extracts' },
  { value: 'other',        label: 'Other',        category: 'Biomass' },
];

/** All product forms available for filtering (legacy + new taxonomy) */
export function getAllFilterableProductForms(): { legacy: ProductFormItem[]; grouped: Record<ProductFormCategory, ProductFormItem[]> } {
  return {
    legacy: LEGACY_PRODUCT_FORMS,
    grouped: getGroupedProductForms(),
  };
}

/** Flat list of all values (for quick lookups) */
export const PRODUCT_FORM_VALUES = PRODUCT_FORM_ITEMS.map((i) => i.value);

/** Spelling/capitalization variants of standard types → standard slug (mirrors the DB cleanup). */
const FORMAT_VARIANTS: Record<string, string> = {
  'a flower': 'a-flower', 'b flower': 'b-flower', 'c flower': 'c-flower',
  'a/b mix': 'a/b flower mix', 'b/c mix': 'b/c flower mix', 'a/b/c mix': 'a/b/c flower mix',
  'a/b flower': 'a/b flower mix', 'b/c flower': 'b/c flower mix', 'a/b/c flower': 'a/b/c flower mix',
  'a/b mix flower': 'a/b flower mix', 'a/b/c mix dried flower': 'a/b/c flower mix',
  'frozen flower': 'fresh frozen', 'fresh frozen flower': 'fresh frozen',
  'pre roll': 'pre-roll, flower', 'raw preroll': 'pre-roll, flower', 'pre-roll': 'pre-roll, flower',
  'large flower': 'flower', 'milled': 'milled flower',
  'thc distillate / extract': 'distillate', 'thc distillate extract': 'distillate',
};

/**
 * The one display name for a product type, wherever it appears. Standard types
 * (and their spelling variants) always show the standard name; seller wording
 * outside the standard list is shown as typed, each word capitalized.
 */
export function getProductFormLabel(value: string | null | undefined): string {
  if (!value) return '';
  const key = value.trim().toLowerCase().replace(/\s+/g, ' ');
  const slug = FORMAT_VARIANTS[key] ?? key;
  const item = PRODUCT_FORM_ITEMS.find((i) => i.value === slug)
    ?? LEGACY_PRODUCT_FORMS.find((i) => i.value === slug);
  if (item) return item.label;
  return value.trim().replace(/(^|[\s/(-])([a-z])/g, (_m, p: string, c: string) => p + c.toUpperCase());
}

/**
 * Backward-compatible flat list matching the old PRODUCT_FORMS shape:
 * { value: string; label: string }[]
 */
const PRODUCT_FORMS_FLAT_MUTABLE: { value: string; label: string }[] = PRODUCT_FORM_ITEMS.map(
  ({ value, label }) => ({ value, label }),
);
export const PRODUCT_FORMS_FLAT = PRODUCT_FORMS_FLAT_MUTABLE;
